/**
 * Jubly SalesDrive — CRM Pipeline (Kanban Board)
 * Drag-and-drop deal management across pipeline stages
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, X, Loader2, ChevronDown, Briefcase, User,
  Calendar, Trophy, XCircle, AlertCircle, Building2,
  ArrowRight, Edit2
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(cents) {
  if (!cents && cents !== 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PRIORITY_STYLES = {
  low:    'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  high:   'bg-red-100 text-red-600',
};

// ─── Deal Card ─────────────────────────────────────────────────────────────────

function DealCard({ deal, onClick, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-3.5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all select-none"
    >
      <p className="text-sm font-semibold text-gray-900 leading-snug mb-2">{deal.title}</p>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-indigo-600">{formatCurrency(deal.value)}</span>
        {deal.priority && (
          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${PRIORITY_STYLES[deal.priority] || 'bg-gray-100 text-gray-600'}`}>
            {deal.priority}
          </span>
        )}
      </div>
      {(deal.contact_name || deal.company_name) && (
        <div className="flex items-center gap-1.5 mt-2">
          <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-500 truncate">
            {deal.contact_name || deal.company_name}
          </span>
        </div>
      )}
      {deal.expected_close_date && (
        <div className="flex items-center gap-1.5 mt-1">
          <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-400">{formatDate(deal.expected_close_date)}</span>
        </div>
      )}
    </div>
  );
}

// ─── Deal Slide-over Panel ───────────────────────────────────────────────────────

function DealSlideOver({ dealId, onClose, onUpdated }) {
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [lossReason, setLossReason] = useState('');
  const [showLossForm, setShowLossForm] = useState(false);

  useEffect(() => {
    fetch(`/api/crm/deals/${dealId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setDeal(d); setLoading(false); });
  }, [dealId]);

  async function handleClose(status) {
    if (status === 'lost' && !showLossForm) {
      setShowLossForm(true);
      return;
    }
    setClosing(true);
    await fetch(`/api/crm/deals/${dealId}/close`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, lossReason: lossReason || undefined }),
    });
    setClosing(false);
    onUpdated();
    onClose();
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Deal Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
          </div>
        ) : !deal ? (
          <div className="flex items-center justify-center flex-1 text-gray-400">
            <p className="text-sm">Deal not found</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Title + Value */}
            <div>
              <h3 className="text-xl font-bold text-gray-900">{deal.title}</h3>
              <p className="text-2xl font-bold text-indigo-600 mt-1">{formatCurrency(deal.value)}</p>
            </div>

            {/* Status badge */}
            {deal.status && deal.status !== 'open' && (
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
                deal.status === 'won' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
              }`}>
                {deal.status === 'won' ? <Trophy className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {deal.status === 'won' ? 'Won' : 'Lost'}
              </div>
            )}

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-4">
              {deal.stage_name && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Stage</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                    {deal.stage_name}
                  </span>
                </div>
              )}
              {deal.priority && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Priority</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_STYLES[deal.priority] || 'bg-gray-100 text-gray-600'}`}>
                    {deal.priority}
                  </span>
                </div>
              )}
              {deal.contact_name && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Contact</p>
                  <p className="text-sm text-gray-700 flex items-center gap-1">
                    <User className="w-3.5 h-3.5 text-gray-400" />{deal.contact_name}
                  </p>
                </div>
              )}
              {deal.company_name && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Company</p>
                  <p className="text-sm text-gray-700 flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5 text-gray-400" />{deal.company_name}
                  </p>
                </div>
              )}
              {deal.expected_close_date && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Expected Close</p>
                  <p className="text-sm text-gray-700">{formatDate(deal.expected_close_date)}</p>
                </div>
              )}
              {deal.actual_close_date && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Closed On</p>
                  <p className="text-sm text-gray-700">{formatDate(deal.actual_close_date)}</p>
                </div>
              )}
            </div>

            {deal.notes && (
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Notes</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{deal.notes}</p>
              </div>
            )}

            {deal.loss_reason && (
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Loss Reason</p>
                <p className="text-sm text-gray-700">{deal.loss_reason}</p>
              </div>
            )}

            {/* Close deal actions */}
            {deal.status === 'open' && (
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Close Deal</p>
                {showLossForm ? (
                  <div className="space-y-2">
                    <textarea
                      rows={2}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                      placeholder="Reason for loss (optional)…"
                      value={lossReason}
                      onChange={e => setLossReason(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowLossForm(false)}
                        className="flex-1 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >Cancel</button>
                      <button
                        onClick={() => handleClose('lost')}
                        disabled={closing}
                        className="flex-1 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-60"
                      >{closing ? 'Saving…' : 'Mark Lost'}</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleClose('won')}
                      disabled={closing}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-60"
                    >
                      <Trophy className="w-4 h-4" /> Won
                    </button>
                    <button
                      onClick={() => handleClose('lost')}
                      disabled={closing}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-60"
                    >
                      <XCircle className="w-4 h-4" /> Lost
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ─── New Deal Modal ────────────────────────────────────────────────────────────

const EMPTY_DEAL = {
  title: '', value: '', contactId: '', contactSearch: '',
  expectedCloseDate: '', priority: 'medium', notes: '',
};

function NewDealModal({ pipeline, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_DEAL);
  const [contacts, setContacts] = useState([]);
  const [contactResults, setContactResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeout = useRef(null);

  const firstStage = pipeline?.stages?.[0];

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function handleContactSearch(val) {
    set('contactSearch', val);
    set('contactId', '');
    clearTimeout(searchTimeout.current);
    if (!val.trim()) { setContactResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      const res = await fetch(`/api/crm/contacts?search=${encodeURIComponent(val)}&limit=8`, { credentials: 'include' });
      const data = await res.json();
      setContactResults(data.contacts || []);
    }, 300);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!firstStage) { setError('Pipeline has no stages'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/crm/deals', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          value: form.value ? Math.round(parseFloat(form.value) * 100) : 0,
          pipelineId: pipeline.id,
          stageId: firstStage.id,
          contactId: form.contactId || undefined,
          expectedCloseDate: form.expectedCloseDate || undefined,
          priority: form.priority,
          notes: form.notes || undefined,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create deal');
      onCreated();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">New Deal</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Deal Title *</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. Acme Corp — Enterprise Plan" autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Value ($)</label>
              <input
                type="number" min="0" step="0.01"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.value} onChange={e => set('value', e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                value={form.priority} onChange={e => set('priority', e.target.value)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* Contact search */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.contactSearch} onChange={e => handleContactSearch(e.target.value)}
              placeholder="Search contacts…"
            />
            {contactResults.length > 0 && !form.contactId && (
              <ul className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                {contactResults.map(c => (
                  <li
                    key={c.id}
                    onClick={() => {
                      set('contactId', c.id);
                      set('contactSearch', [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email);
                      setContactResults([]);
                    }}
                    className="px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer"
                  >
                    <span className="font-medium">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '(No name)'}</span>
                    {c.email && <span className="text-gray-400 ml-2">{c.email}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Expected Close Date</label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.expectedCloseDate} onChange={e => set('expectedCloseDate', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Any notes…"
            />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create Deal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Kanban Column ─────────────────────────────────────────────────────────────

function KanbanColumn({ stage, deals, onDealClick, onDragStart, onDragOver, onDrop }) {
  const totalValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);

  return (
    <div
      className="flex flex-col w-64 flex-shrink-0 bg-gray-50 rounded-2xl border border-gray-200"
      onDragOver={e => { e.preventDefault(); onDragOver(stage.id); }}
      onDrop={e => onDrop(stage.id)}
    >
      {/* Column Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: stage.color || '#6366F1' }}
          />
          <span className="text-sm font-semibold text-gray-800 truncate flex-1">{stage.name}</span>
          <span className="text-xs font-medium text-gray-400 bg-gray-200 rounded-full px-1.5 py-0.5">
            {deals.length}
          </span>
        </div>
        {totalValue > 0 && (
          <p className="text-xs text-gray-400 mt-1 pl-4.5">{formatCurrency(totalValue)}</p>
        )}
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[120px]">
        {deals.map((deal, idx) => (
          <DealCard
            key={deal.id}
            deal={deal}
            onClick={() => onDealClick(deal.id)}
            onDragStart={() => onDragStart(deal, idx)}
          />
        ))}
        {deals.length === 0 && (
          <div className="flex items-center justify-center h-20 border-2 border-dashed border-gray-200 rounded-xl">
            <p className="text-xs text-gray-300">Drop here</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function SalesPipeline() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pipelines, setPipelines] = useState([]);
  const [activePipelineId, setActivePipelineId] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDealId, _setSelectedDealId] = useState(() => searchParams.get('deal') || null);

  // Sync deal selection to URL for deep linking
  const setSelectedDealId = useCallback((id) => {
    _setSelectedDealId(id);
    const params = new URLSearchParams(searchParams);
    if (id) { params.set('deal', id); } else { params.delete('deal'); }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  const [showNewDeal, setShowNewDeal] = useState(searchParams.get('new') === '1');

  // Drag state
  const dragDeal = useRef(null);
  const dragFromStage = useRef(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  // Load pipelines list
  useEffect(() => {
    fetch('/api/crm/pipelines', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setPipelines(list);
        if (list.length > 0) setActivePipelineId(list[0].id);
        else setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // Load pipeline detail + deals
  const loadPipeline = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const [pRes, dRes] = await Promise.all([
        fetch(`/api/crm/pipelines/${pid}`, { credentials: 'include' }),
        fetch(`/api/crm/deals?pipelineId=${pid}&limit=200`, { credentials: 'include' }),
      ]);
      const pData = await pRes.json();
      const dData = await dRes.json();
      setPipeline(pData);
      setDeals(Array.isArray(dData) ? dData : dData.deals || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (activePipelineId) loadPipeline(activePipelineId);
  }, [activePipelineId, loadPipeline]);

  // Group deals by stage
  const dealsByStage = {};
  if (pipeline?.stages) {
    pipeline.stages.forEach(s => { dealsByStage[s.id] = []; });
    deals.filter(d => d.status === 'open').forEach(d => {
      if (dealsByStage[d.stage_id]) dealsByStage[d.stage_id].push(d);
    });
  }

  // Drag handlers
  function handleDragStart(deal, idx) {
    dragDeal.current = deal;
    dragFromStage.current = deal.stage_id;
  }

  function handleDragOver(stageId) {
    setDragOverStage(stageId);
  }

  async function handleDrop(toStageId) {
    const deal = dragDeal.current;
    if (!deal || deal.stage_id === toStageId) {
      setDragOverStage(null);
      return;
    }
    const dropIndex = (dealsByStage[toStageId] || []).length;
    // Optimistic update
    setDeals(prev => prev.map(d =>
      d.id === deal.id ? { ...d, stage_id: toStageId } : d
    ));
    setDragOverStage(null);
    // Persist
    await fetch(`/api/crm/deals/${deal.id}/stage`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stageId: toStageId, position: dropIndex }),
    });
    dragDeal.current = null;
    dragFromStage.current = null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-sm font-medium text-gray-700">Failed to load pipeline</p>
        <p className="text-xs text-gray-400 mt-1">{error}</p>
      </div>
    );
  }

  if (pipelines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <Briefcase className="w-10 h-10 text-gray-300 mb-3 stroke-1" />
        <p className="text-sm font-medium text-gray-600">No pipelines yet</p>
        <p className="text-xs text-gray-400 mt-1">Create a pipeline to get started</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-100 bg-white flex-shrink-0">
        {/* Pipeline selector */}
        {pipelines.length > 1 && (
          <div className="relative">
            <select
              className="appearance-none text-sm font-medium text-gray-700 border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              value={activePipelineId || ''}
              onChange={e => setActivePipelineId(e.target.value)}
            >
              {pipelines.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        )}
        {pipelines.length === 1 && (
          <h1 className="text-base font-semibold text-gray-900">{pipeline?.name || 'Pipeline'}</h1>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {deals.filter(d => d.status === 'open').length} open deals
            {' · '}
            {formatCurrency(deals.filter(d => d.status === 'open').reduce((s, d) => s + (d.value || 0), 0))}
          </span>
          <button
            onClick={() => setShowNewDeal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Deal
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-4 p-6 h-full">
          {(pipeline?.stages || []).map(stage => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              deals={dealsByStage[stage.id] || []}
              onDealClick={id => setSelectedDealId(id)}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      </div>

      {/* Deal slide-over */}
      {selectedDealId && (
        <DealSlideOver
          dealId={selectedDealId}
          onClose={() => setSelectedDealId(null)}
          onUpdated={() => loadPipeline(activePipelineId)}
        />
      )}

      {/* New deal modal */}
      {showNewDeal && pipeline && (
        <NewDealModal
          pipeline={pipeline}
          onClose={() => { setShowNewDeal(false); setSearchParams({}); }}
          onCreated={() => loadPipeline(activePipelineId)}
        />
      )}
    </div>
  );
}
